/**
 * Workflow drawing (user, 2026-10-29 -- second version, after the boss's
 * ChatGPT pictures): a chain of blocks joined by arrows, top to bottom.
 *
 * Node types (React Flow `type`, with their `data`):
 * - trigger   { name }                    "Order Approved" -- the start, one per workflow
 * - step      { name, note, stationId }   a station dragged in from the left
 *                                          (user, 2026-10-30): the station's
 *                                          name is its heading, `name` the
 *                                          label under it; a station can be
 *                                          used any number of times
 * - branch    { name, icon, color }       a coloured heading over a branch
 * - milestone { name }                    "Sample Ready" -- a check after a branch
 * - ready     { name, note }              "Ready for Production" -- the end,
 *                                          one per workflow (always there)
 * Edges: { id, source, target } = "source comes before target".
 *
 * Test run rules:
 * - a block is open when every block with an arrow into it is done
 * - the trigger counts as done (the order is approved)
 * - branch, milestone and ready blocks have no buttons: they are done as
 *   soon as they are open
 * - a step is Pending (locked) until open, then Working, then Done when
 *   marked; Undo is off once a step after it is done
 */

export const AUTO_TYPES = ["trigger", "branch", "milestone", "ready"];

export const newId = (prefix) =>
  `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

export const BRANCH_COLORS = {
  purple: { bg: "#ede9fe", fg: "#6d28d9" },
  blue: { bg: "#dbeafe", fg: "#1d4ed8" },
  green: { bg: "#dcfce7", fg: "#15803d" },
  orange: { bg: "#ffedd5", fg: "#c2410c" },
  pink: { bg: "#fce7f3", fg: "#be185d" },
  gray: { bg: "#f3f4f6", fg: "#374151" },
};

// mdi icon names (without "mdi-") offered for stations and branches.
export const ICONS = [
  "printer",
  "fire",
  "file-document-outline",
  "file-code-outline",
  "needle",
  "content-cut",
  "tshirt-crew",
  "cart-outline",
  "package-variant",
  "truck-outline",
  "palette",
  "cog-outline",
  "check-circle-outline",
];

export const DEFAULT_DATA = {
  trigger: () => ({ name: "Order Approved" }),
  step: () => ({ name: "", note: "", stationId: null }),
  branch: () => ({ name: "New branch", icon: "source-branch", color: "purple" }),
  milestone: () => ({ name: "Ready" }),
  ready: () => ({ name: "Ready for Production", note: "When all branches are complete" }),
};

export const makeNode = (type, position, data = {}) => ({
  id: newId(type[0]),
  type,
  position,
  data: { ...DEFAULT_DATA[type](), ...data },
});

// Would source -> target close a loop (or join a block to itself)?
export function wouldLoop(edges, source, target) {
  if (source === target) return true;
  const seen = new Set();
  const stack = [target];
  while (stack.length) {
    const at = stack.pop();
    if (at === source) return true;
    if (seen.has(at)) continue;
    seen.add(at);
    edges.filter((e) => e.source === at).forEach((e) => stack.push(e.target));
  }
  return false;
}

/**
 * Status of every block for a set of done step ids:
 * Map id -> { status: "pending" | "working" | "done", canMark, canUnmark, hint }
 */
export function computeStatuses(nodes, edges, doneIds) {
  const done = doneIds instanceof Set ? doneIds : new Set(doneIds || []);
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const into = (id) => edges.filter((e) => e.target === id).map((e) => byId.get(e.source)).filter(Boolean);
  const outOf = (id) => edges.filter((e) => e.source === id).map((e) => byId.get(e.target)).filter(Boolean);

  // Is a block done? Memoised; a loop (never saved, but be safe) counts as not done.
  const memo = new Map();
  const isDone = (n, path = new Set()) => {
    if (memo.has(n.id)) return memo.get(n.id);
    if (path.has(n.id)) return false;
    path.add(n.id);
    let result;
    if (n.type === "trigger") result = true;
    else if (n.type === "step") result = done.has(n.id);
    else result = into(n.id).every((p) => isDone(p, path));
    path.delete(n.id);
    memo.set(n.id, result);
    return result;
  };
  const isOpen = (n) => into(n.id).every((p) => isDone(p));

  // The steps right after a block, looking through branch / milestone / ready.
  const nextSteps = (id, seen = new Set()) =>
    outOf(id).flatMap((n) => {
      if (seen.has(n.id)) return [];
      seen.add(n.id);
      return n.type === "step" ? [n] : nextSteps(n.id, seen);
    });

  const out = new Map();
  nodes.forEach((n) => {
    if (n.type !== "step") {
      out.set(n.id, { status: isDone(n) ? "done" : "pending", canMark: false, canUnmark: false, hint: "" });
      return;
    }
    const open = isOpen(n);
    const status = done.has(n.id) ? "done" : open ? "working" : "pending";
    const doneAfter = nextSteps(n.id).find((s) => done.has(s.id));
    let hint = "";
    if (status === "pending") {
      const waiting = into(n.id).filter((p) => !isDone(p)).map((p) => p.data?.name || "the block above");
      hint = `Waiting for ${waiting.join(", ")}`;
    } else if (status === "done" && doneAfter) {
      hint = `${doneAfter.data?.name || "The next step"} is already done`;
    }
    out.set(n.id, {
      status,
      canMark: status === "working",
      canUnmark: status === "done" && !doneAfter,
      hint,
    });
  });
  return out;
}

/**
 * Tidy layout: each block one row below the lowest block pointing into it;
 * blocks in a row ordered by where their parents sit, centred under the
 * trigger. Returns a Map id -> { x, y }.
 */
// rowHeight leaves room for a step with a note, its station and the Test
// run buttons.
export function tidyPositions(nodes, edges, { colWidth = 270, rowHeight = 185 } = {}) {
  const ids = new Set(nodes.map((n) => n.id));
  const parents = new Map(nodes.map((n) => [n.id, []]));
  edges.forEach((e) => {
    if (ids.has(e.source) && ids.has(e.target)) parents.get(e.target).push(e.source);
  });

  const rank = new Map();
  const rankOf = (id, path = new Set()) => {
    if (rank.has(id)) return rank.get(id);
    if (path.has(id)) return 0;
    path.add(id);
    const ps = parents.get(id) || [];
    const r = ps.length ? Math.max(...ps.map((p) => rankOf(p, path))) + 1 : 0;
    path.delete(id);
    rank.set(id, r);
    return r;
  };
  nodes.forEach((n) => rankOf(n.id));

  const rows = [];
  nodes.forEach((n) => {
    const r = rank.get(n.id);
    (rows[r] = rows[r] || []).push(n);
  });

  const x = new Map();
  const out = new Map();
  rows.forEach((row, r) => {
    if (!row) return;
    // Each block wants to sit under the average of its parents; blocks
    // without parents spread around the middle.
    const order = [...row].sort((a, b) => (a.position?.x ?? 0) - (b.position?.x ?? 0));
    const want = new Map(
      order.map((n, i) => {
        const ps = (parents.get(n.id) || []).filter((p) => x.has(p));
        const centred = (i - (order.length - 1) / 2) * colWidth;
        return [n.id, ps.length ? ps.reduce((s, p) => s + x.get(p), 0) / ps.length : centred];
      }),
    );
    const sorted = [...row].sort((a, b) => want.get(a.id) - want.get(b.id));
    // Push apart so no two blocks overlap, then shift the row back so it
    // stays centred on where it wanted to be.
    const placed = [];
    sorted.forEach((n, i) => {
      const w = want.get(n.id);
      placed.push(i === 0 ? w : Math.max(w, placed[i - 1] + colWidth));
    });
    const shift =
      sorted.reduce((s, n) => s + want.get(n.id), 0) / sorted.length -
      placed.reduce((s, v) => s + v, 0) / placed.length;
    sorted.forEach((n, i) => {
      const col = placed[i] + shift;
      x.set(n.id, col);
      out.set(n.id, { x: col, y: r * rowHeight });
    });
  });
  return out;
}

// What the API stores: only the fields the builder needs back.
export const flowToSave = (nodes, edges) => ({
  nodes: nodes.map(({ id, type, position, data }) => ({ id, type, position, data: { ...data } })),
  edges: edges.map(({ id, source, target }) => ({ id, source, target })),
});
